import React, { useEffect, useRef, useState } from 'react';

import { Button, ButtonGroup, Dialog, Message, TextInput } from 'Components/Core';
import { PageSection } from 'Components/Layout';

const DialogWithAutoFocusInput: React.FC<{
  visible: boolean;
  setVisible: (v: boolean) => void;
}> = ({ visible, setVisible }) => {
  const autoFocusElement = useRef<HTMLInputElement | null>(null);
  return (
    <Dialog
      buttons={[
        <Button key="1" onClick={() => setVisible(false)}>
          Cancel
        </Button>,
      ]}
      onClose={() => setVisible(false)}
      title="Dialog with an autoFocus input"
      visible={visible}
      autoFocusRef={autoFocusElement}
    >
      <TextInput focusRef={autoFocusElement} />
    </Dialog>
  );
};

const DialogFocusTrapTest: React.FC = () => {
  const [isWithoutInputVisible, setWithoutInputVisible] = useState(false);
  const [isWithInputVisible, setWithInputInvisible] = useState(false);
  const [isWithAutoFocusInputVisible, setWithAutoFocusInputVisible] = useState(false);
  const [isEmptyVisible, setEmptyVisible] = useState(false);

  useEffect(() => {
    if (isEmptyVisible) setTimeout(() => setEmptyVisible(false), 1000);
  }, [isEmptyVisible]);

  return (
    <>
      <Message severity="info">This page is for testing dialog focus traps.</Message>
      <PageSection heading="Test Dialogs">
        <ButtonGroup>
          <Button onClick={() => setWithoutInputVisible(true)}>Show Dialog without Input</Button>
          <Button onClick={() => setWithInputInvisible(true)}>Show Dialog with Input</Button>
          <Button onClick={() => setWithAutoFocusInputVisible(true)}>Show Dialog with Auto-Focussed Input</Button>
          <Button onClick={() => setEmptyVisible(true)}>Show Empty Dialog</Button>
          <Button
            onClick={() => {
              setWithoutInputVisible(true);
              setTimeout(() => {
                setWithInputInvisible(true);
                setTimeout(() => {
                  setWithAutoFocusInputVisible(true);
                  setTimeout(() => {
                    setEmptyVisible(true);
                  }, 1000);
                }, 1000);
              }, 1000);
            }}
          >
            Show Stack (time delay)
          </Button>
        </ButtonGroup>

        <Dialog
          buttons={[
            <Button key="1" onClick={() => setWithInputInvisible(false)}>
              Cancel
            </Button>,
          ]}
          onClose={() => setWithInputInvisible(false)}
          title="Dialog with an input"
          visible={isWithInputVisible}
        >
          <TextInput />
        </Dialog>

        <Dialog
          buttons={[
            <Button key="1" onClick={() => setWithoutInputVisible(false)} autoFocus>
              Cancel
            </Button>,
          ]}
          onClose={() => setWithoutInputVisible(false)}
          title="Dialog without an input"
          visible={isWithoutInputVisible}
        >
          Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore
          magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo
          consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla
          pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id
          est laborum.
        </Dialog>

        <DialogWithAutoFocusInput visible={isWithAutoFocusInputVisible} setVisible={setWithAutoFocusInputVisible} />

        <Dialog visible={isEmptyVisible} title="Empty dialog">
          Empty dialog (no tabbable content). I will close automatically.
        </Dialog>
      </PageSection>
    </>
  );
};

export default DialogFocusTrapTest;
